/**
 * Pluggable inference runner interface for the blood-smear pipeline.
 *
 * Phase 3a.1 ships ONLY a `NullRunner` that reports `loaded: false`. The
 * actual ONNX-backed runner lands in Phase 3a.2, gated on:
 *   - a licensed model artifact (CC-BY-4.0 / Apache-2.0 / MIT or a written DUA),
 *   - a published dataset card, intended-use statement and fairness slice,
 *   - per-species sensitivity/specificity at WHO 200/µL and 2000/µL thresholds,
 *   - microscopist concordance on at least 100 cases.
 *
 * Until then, the UI must display a "Model not deployed — research preview"
 * state and never present a species call or density figure.
 */

export type PlasmodiumSpecies = 'P. falciparum' | 'P. vivax' | 'P. malariae' | 'P. ovale' | 'P. knowlesi';

export type Detection = {
  /** Bounding box in image-pixel coordinates. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Predicted parasite species. */
  species: PlasmodiumSpecies;
  /** Model confidence in [0, 1]. */
  confidence: number;
  /** Lifecycle stage when the model supports it. */
  stage?: 'ring' | 'trophozoite' | 'schizont' | 'gametocyte';
};

export type RunnerStatus =
  | { loaded: false; reason: string }
  | { loaded: true; name: string; version: string; license: string };

export type InferenceInput = {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  micronsPerPixel?: number;
};

export interface BloodSmearRunner {
  status(): RunnerStatus;
  loadModel(): Promise<RunnerStatus>;
  infer(tile: InferenceInput): Promise<Detection[]>;
}

/**
 * Default runner used until a licensed artifact is registered.
 * Returns no detections and is explicit about why.
 */
export class NullRunner implements BloodSmearRunner {
  status(): RunnerStatus {
    return {
      loaded: false,
      reason:
        'No licensed Plasmodium detection model is deployed in this build. ' +
        'Phase 3a.2 will wire in a vetted ONNX artifact with a published fairness report.',
    };
  }
  async loadModel(): Promise<RunnerStatus> {
    return this.status();
  }
  async infer(): Promise<Detection[]> {
    return [];
  }
}

let activeRunner: BloodSmearRunner = new NullRunner();

/** Replace the active runner. Used by Phase 3a.2 to register the ONNX runner. */
export function registerRunner(runner: BloodSmearRunner) {
  activeRunner = runner;
}

export function getRunner(): BloodSmearRunner {
  return activeRunner;
}
